import { useCallback, useEffect, useRef, useState } from 'react';
import { AmpAccessToken, AuthCallbacks, AuthProvider, AuthProviderState, AuthState } from './Auth';

const NO_ROLES: string[] = [];

export const useAuthCallbacks = (authProvider: AuthProvider): AuthCallbacks => {
  const [authProviderState, setProviderAuthState] = useState<AuthProviderState>({
    user: null,
    authState: AuthState.LOADING,
    session: null,
  });
  const [loginUsername, setLoginUsername] = useState<string | null>(null);
  const [loginEmail, setLoginEmail] = useState<string | null>(null);
  const [loginRoles, setLoginRoles] = useState<string[]>(NO_ROLES);
  const [authState, setAuthState] = useState(authProviderState.authState);
  const accessToken = useRef<AmpAccessToken | null>(null);
  const accessTokenTimeout = useRef<NodeJS.Timeout | null>(null);

  const updateStateFromProvider = useCallback(
    (providerState: AuthProviderState) => {
      setProviderAuthState(providerState);
      setAuthState(providerState.authState);
      if (providerState.authState === AuthState.SIGNED_IN && providerState.session) {
        setLoginUsername(providerState.session.username);
        setLoginEmail(providerState.session.email ?? null);
        setLoginRoles(providerState.session.roles ?? NO_ROLES);
      } else {
        setLoginUsername(null);
        setLoginEmail(null);
        setLoginRoles(NO_ROLES);
      }
    },
    [setProviderAuthState, setAuthState, setLoginUsername, setLoginEmail, setLoginRoles]
  );

  const resetAuthState = useCallback(() => {
    updateStateFromProvider({
      user: null,
      session: null,
      authState: AuthState.SIGN_IN,
    });
  }, [updateStateFromProvider]);

  const signIn = useCallback(
    async (rawUsername: string) => {
      const username = rawUsername.toLowerCase();
      await authProvider.signIn(username);
    },
    [authProvider]
  );

  const signInIdp = useCallback(
    async (email: string) => {
      await authProvider.signInIdp(email);
    },
    [authProvider]
  );

  const signInWithWorkspace = useCallback(
    async (workspace: string, email: string) => {
      await authProvider.signInWithWorkspace(workspace, email);
    },
    [authProvider]
  );

  const signInIdpHelper = useCallback(
    async (adminUsername: string, email: string) => {
      await authProvider.signInIdpHelper(adminUsername, email);
    },
    [authProvider]
  );

  const signOut = useCallback(() => {
    authProvider.signOut();
    resetAuthState();
  }, [authProvider, resetAuthState]);

  const getToken = useCallback(
    async (force?: boolean): Promise<AmpAccessToken> => {
      try {
        const token = await authProvider.getToken(force || false);
        accessToken.current = token;
        return token;
      } catch (err) {
        authProvider.signOut();
        resetAuthState();
        throw err;
      }
    },
    [authProvider, resetAuthState, accessToken]
  );

  useEffect(() => {
    async function initAuthState() {
      // Keep LOADING until the provider resolves so the login page doesn't flash before a signed-in session loads.
      const newAuthState = await authProvider.getInitialAuthState().catch(() => null);

      if (!newAuthState?.user || !newAuthState.session) {
        // if the provider failed or returned no user or session, always force sign in
        // other states should not be supported in an initial auth flow
        resetAuthState();
        return;
      }

      updateStateFromProvider(newAuthState);
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    initAuthState();
  }, [authProvider, resetAuthState, updateStateFromProvider]);

  // TODO(TSP-1778, TSP-1788): Remove this effect once cmp / artifacts is requesting token
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!accessToken.current) {
        return;
      }

      if (!accessTokenTimeout.current) {
        // fetch the new token just shortly before expiration to make ensure all calls are authorized
        accessTokenTimeout.current = setTimeout(() => {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          getToken(true).then(() => {
            accessTokenTimeout.current = null;
          });
        }, accessToken.current.expiration.getTime() - new Date().getTime() - 30000);
      }
    }, 500);

    return () => {
      clearInterval(intervalId);
      clearTimeout(accessTokenTimeout.current!);
    };
  }, [authProvider, getToken, accessToken, accessTokenTimeout]);

  return {
    authState,
    loginUsername,
    loginEmail,
    loginRoles,
    resetAuthState,
    signIn,
    signInIdp,
    signOut,
    getToken,
    accessToken,
    signInWithWorkspace,
    signInIdpHelper,
  };
};
